import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import RubricMarkdown from "./RubricMarkdown";
import MessageBox from "./MessageBox";
import ConfirmDialog from "./ConfirmDialog";
import DateFilterCalendar from "./DateFilterCalendar";
import { ClassSectionFilters, SubjectColumn, itemMatchesClass, itemMatchesAssignmentWord, itemsInClass } from "./ClassFolderSearch";
import { copyToClipboard } from "../utils/clipboard";
import QuestionPaperPreview from "./QuestionPaperPreview";
import { extractQuestionPaper, attachRubricCard, stripQuestionPaperText, restorePaperNotes } from "../utils/questionPaper";
import { toPlainMath } from "../utils/plainMath";
import { alignRubricPaperTotal, splitRubricQuestions, replaceRubricBlockAt, replaceRubricBlocks } from "../utils/rubricQuestions";
import { chatWithRubricAI } from "../utils/backendApiWrapper";
import { CLOSED_WORLD_BREAKDOWN } from "../utils/closedWorldRubric";
import { rubricNameWithDate, folderDisplayTitle } from "../utils/folderDisplay";
import SaveRubricModal from "./SaveRubricModal";
import { ocrMissingFolderTitle } from "../utils/ocrMissing";
import { LOCAL_MODELS, GEMINI_MODELS, OPENAI_MODELS, isRubricModelChoice } from "../utils/localModels";
import { stageModelLabel } from "../utils/gradingMachine";
import "./RubricChatbot.css";
import MultiCheckSelect from "./MultiCheckSelect";

function shownRubric(text) {
  return alignRubricPaperTotal(toPlainMath(text));
}

function regenHistoryOf(rubric) {
  const raw = rubric && rubric.regen_history;
  let list = raw;
  if (typeof raw === "string") {
    try { list = JSON.parse(raw); } catch (_) { return []; }
  }
  if (!Array.isArray(list)) return [];
  return list.filter((entry) => entry && (
    String(entry.content || "").trim()
    || (entry.at && Array.isArray(entry.questions) && entry.questions.length)
  ));
}

function rubricContentRuns(rubric) {
  if (!rubric) return [];
  const current = String(rubric.content || "");
  const snapshots = regenHistoryOf(rubric)
    .filter((entry) => String(entry.content || "").trim())
    .slice(-3);
  if (!snapshots.length) {
    return [{ at: rubric.created_at || null, model: "", host: "", content: current, latest: true }];
  }
  const runs = snapshots.map((entry) => ({
    at: entry.at,
    model: entry.model || "",
    host: entry.host || "",
    content: String(entry.content || "")
  }));
  runs[runs.length - 1].content = current || runs[runs.length - 1].content;
  return runs.map((run, index) => ({ ...run, latest: index === runs.length - 1 }));
}

function alignRubricRuns(runs) {
  const parsed = runs.map((run) => splitRubricQuestions(shownRubric(run.content)));
  const keys = [];
  const seen = new Set();
  const push = (id) => {
    const key = String(id || "").trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };
  const latest = parsed[parsed.length - 1];
  if (latest) latest.blocks.forEach((block) => push(block.id));
  parsed.forEach((parts) => parts.blocks.forEach((block) => push(block.id)));
  return { parsed, keys };
}

function splitQuestionStatement(text) {
  const raw = String(text || "").replace(/\r\n/g, "\n").replace(/^\n+/, "");
  const breakAt = raw.indexOf("\n");
  if (breakAt === -1) return { statement: raw.trim(), rest: "" };
  return {
    statement: raw.slice(0, breakAt).trim(),
    rest: raw.slice(breakAt + 1).replace(/^\n+/, "")
  };
}

function RubricRunBody({ content }) {
  const parts = splitRubricQuestions(shownRubric(content));
  if (!parts.blocks.length) {
    return <RubricMarkdown>{shownRubric(content)}</RubricMarkdown>;
  }
  return (
    <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-primary)" }}>
      {parts.preamble.trim() ? <RubricMarkdown>{parts.preamble}</RubricMarkdown> : null}
      {parts.blocks.map((b, bi) => (
        <div key={`${b.id}-${bi}`} className="rc-qblock">
          <div className="rc-qblock__body">
            <RubricMarkdown>{shownRubric(b.text)}</RubricMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
}

function RegenTimeChip({ entry, label }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{ position: "relative", display: "inline-flex", zIndex: open ? 40 : 1 }}
    >
      <span
        style={{
          fontSize: 12,
          color: "#5b21b6",
          background: "#f5f3ff",
          border: "1px solid #c4b5fd",
          borderRadius: 6,
          padding: "3px 8px",
          cursor: "default",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {open && (
        <span
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 30,
            minWidth: 120,
            maxWidth: 280,
            maxHeight: 220,
            overflowY: "auto",
            background: "var(--panel-bg, #fff)",
            color: "var(--text-primary, #111)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: 8,
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
            padding: "6px 10px 8px",
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {entry.questions.map((q, i) => (
            <span key={`${q}-${i}`} style={{ display: "block", whiteSpace: "nowrap" }}>{q}</span>
          ))}
        </span>
      )}
    </span>
  );
}

function canManageAnyRubric(role) {
  return role === "administrator" || role === "examiner" || role === "management";
}

function normSubject(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function ruleRows(ruleSet) {
  if (!ruleSet) return [];
  if (Array.isArray(ruleSet.rules)) return ruleSet.rules;
  if (typeof ruleSet.rules === "string") {
    try {
      const parsed = JSON.parse(ruleSet.rules);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

function ruleSetForRubric(sets, rubric) {
  const usable = (Array.isArray(sets) ? sets : []).filter((rs) => ruleRows(rs).length);
  let savedId = "";
  try { savedId = localStorage.getItem("rubricActiveRuleId") || ""; } catch (_) { savedId = ""; }
  const saved = usable.find((rs) => String(rs.id) === String(savedId));
  if (saved) return saved;
  const subject = normSubject(rubric && rubric.subject);
  if (!subject) return null;
  const matches = usable.filter((rs) => {
    const names = [rs.display_name, rs.subject_key, rs.subject].map(normSubject).filter(Boolean);
    return names.some((name) => name === subject || name.includes(subject) || subject.includes(name));
  });
  if (!matches.length) return null;
  return matches.find((rs) => {
    const names = [rs.display_name, rs.subject_key, rs.subject].map(normSubject);
    return names.includes(subject);
  }) || matches[0];
}

const API_BASE_URL = window.API_BASE_URL;

// Date, teacher, class, subject, type and sort are applied to the list
// already on the page. The network fetch stays out of this function.
function rubricDateKey(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function viewRubrics(allRubrics, { filterMode, selectedDate, selectedEndDate, filterTeacher, filterSubject, filterClass, filterType, sortBy, gradeFilter, sectionFilter, subjectPick, assignmentWord }) {
  let rubrics = [...(allRubrics || [])];

  rubrics.sort((a, b) => {
    if (a.status === 'pending' && b.status === 'used') return -1;
    if (a.status === 'used' && b.status === 'pending') return 1;
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  });

  if (filterTeacher) {
    rubrics = rubrics.filter((rubric) => rubric.created_by === filterTeacher);
  }
  if (filterSubject && filterSubject.length) {
    const names = new Set(filterSubject.map((item) => item.value));
    rubrics = rubrics.filter((rubric) => names.has(rubric.subject));
  }
  if (filterClass && filterClass.length) {
    const names = new Set(filterClass.map((item) => item.value));
    rubrics = rubrics.filter((rubric) => names.has(rubric.class));
  }
  if (filterType === 'mcq') {
    rubrics = rubrics.filter((rubric) => rubric.type === 'mcq');
  } else if (filterType === 'subjective') {
    rubrics = rubrics.filter((rubric) => rubric.type !== 'mcq');
  }

  if (gradeFilter) {
    rubrics = rubrics.filter((rubric) => itemMatchesClass(rubric, gradeFilter, sectionFilter, subjectPick));
  } else if (filterMode === "none") {
    rubrics = [];
  } else if (filterMode === "today") {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    rubrics = rubrics.filter((rubric) => {
      if (!rubric.test_date) return false;
      const validDate = new Date(rubric.test_date);
      validDate.setHours(0, 0, 0, 0);
      return validDate.getTime() === today.getTime();
    });
  } else if (filterMode === "date" && selectedDate) {
    // One day, or every day from start through end — same as Sort Papers.
    const startKey = selectedDate;
    const endKey = selectedEndDate || selectedDate;
    rubrics = rubrics.filter((rubric) => {
      const key = rubricDateKey(rubric.test_date);
      return key && key >= startKey && key <= endKey;
    });
  } else if (filterMode === "future") {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    rubrics = rubrics.filter((rubric) => {
      if (!rubric.test_date) return false;
      const rubricDate = new Date(rubric.test_date);
      rubricDate.setHours(0, 0, 0, 0);
      return rubricDate.getTime() >= tomorrow.getTime();
    });
  }

  if (assignmentWord) {
    rubrics = rubrics.filter((rubric) => itemMatchesAssignmentWord(rubric, assignmentWord));
  }

  if (sortBy === 'date') {
    rubrics.sort((a, b) => {
      const ta = a.test_date ? new Date(a.test_date).getTime() : 0;
      const tb = b.test_date ? new Date(b.test_date).getTime() : 0;
      return tb - ta;
    });
  } else if (sortBy === 'teacher') {
    rubrics.sort((a, b) => {
      return String(a.created_by || '').localeCompare(String(b.created_by || ''), undefined, { sensitivity: 'base' });
    });
  } else if (sortBy === 'class') {
    rubrics.sort((a, b) => {
      return String(a.class || '').localeCompare(String(b.class || ''), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  return rubrics;
}

export default function SavedRubrics({ currentUser }) {
  const navigate = useNavigate();
  const [rubricsLoaded, setRubricsLoaded] = useState(false);
  const [modalRubric, setModalRubric] = useState(null);
  const [questionPaper, setQuestionPaper] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const todayKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  // Opening Saved shows the calendar only. Cards appear after a day, All,
  // Today, or Future Cards is chosen.
  const [filterMode, setFilterMode] = useState("none"); // "none", "all", "today", "date", or "future"
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [isEditingPdfName, setIsEditingPdfName] = useState(false);
  const [editedPdfName, setEditedPdfName] = useState("");
  const [filterTeacher, setFilterTeacher] = useState("");
  const [filterSubject, setFilterSubject] = useState([]);
  const [filterClass, setFilterClass] = useState([]);
  // Class / section / subject buttons, same as Student Lists. A class click
  // shows that class on every date. A calendar click clears these.
  const [gradeFilter, setGradeFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [subjectPick, setSubjectPick] = useState(null);
  // First word of the assignment name. Narrows whichever list is showing.
  const [assignmentWord, setAssignmentWord] = useState(null);
  // Rubric-type segmented filter: 'all' | 'mcq' | 'subjective'. Driven by
  // the rubric.type column ('mcq' for MCQ rubrics, anything else treated
  // as subjective for backward compatibility).
  const [filterType, setFilterType] = useState("subjective");
  // Sort-by selector for the visible rubric grid: 'date' (created_at desc),
  // 'teacher' (created_by alpha), 'class' (class natural). Rendered as a
  // segmented control under the calendar's left rail.
  const [sortBy, setSortBy] = useState("date");
  const [allRubrics, setAllRubrics] = useState([]);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [pickedQuestionIds, setPickedQuestionIds] = useState({});
  const [editingBlockIndex, setEditingBlockIndex] = useState(null);
  const [questionDraft, setQuestionDraft] = useState("");
  const [classSwitchQuery, setClassSwitchQuery] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenScope, setRegenScope] = useState("");
  const [ruleSets, setRuleSets] = useState([]);
  const [modalRuleId, setModalRuleId] = useState("");
  const [rulesMenuOpen, setRulesMenuOpen] = useState(false);
  const isExaminer = currentUser?.role === "examiner";
  const [rubricModelChoice, setRubricModelChoice] = useState(() => {
    try {
      const saved = localStorage.getItem("examinerRubricModel");
      if (saved === "openai") return "openai:gpt-5.4";
      if (isRubricModelChoice(saved)) return saved;
    } catch (_) { /* the default below is used */ }
    return "local:ggml";
  });
  const [rubricHost, setRubricHost] = useState(() => {
    try {
      const saved = localStorage.getItem("examinerRubricHost");
      if (saved === "gpu" || saved === "mac") return saved;
    } catch (_) { /* the default below is used */ }
    return "gpu";
  });
  useEffect(() => {
    if (!isExaminer) return;
    try { localStorage.setItem("examinerRubricModel", rubricModelChoice); } catch (_) { /* the choice still applies to this window */ }
  }, [isExaminer, rubricModelChoice]);
  useEffect(() => {
    if (!isExaminer) return;
    try { localStorage.setItem("examinerRubricHost", rubricHost); } catch (_) { /* the choice still applies to this window */ }
  }, [isExaminer, rubricHost]);

  // Rubrics narrowed by the teacher / class / subject / type chips. The
  // calendar day counts, Future Cards badge, and card list all read from
  // this so a tick updates everything the same way Sort Papers does.
  const visibleRubrics = React.useMemo(() => {
    return (allRubrics || []).filter((r) => {
      if (filterTeacher && r.created_by !== filterTeacher) return false;
      if (filterSubject.length && !filterSubject.some((item) => item.value === r.subject)) return false;
      if (filterClass.length && !filterClass.some((item) => item.value === r.class)) return false;
      if (filterType === 'mcq' && r.type !== 'mcq') return false;
      if (filterType === 'subjective' && r.type === 'mcq') return false;
      return true;
    });
  }, [allRubrics, filterTeacher, filterSubject, filterClass, filterType]);

  // Count of rubrics dated tomorrow onwards (respecting the filter chips)
  // — shown as a badge on the "Future Cards" button.
  const futureRubricCount = React.useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return visibleRubrics.filter((r) => {
      if (!r.test_date) return false;
      const d = new Date(r.test_date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() >= tomorrow.getTime();
    }).length;
  }, [visibleRubrics]);

  const savedRubrics = useMemo(() => viewRubrics(allRubrics, {
    filterMode,
    selectedDate,
    selectedEndDate,
    filterTeacher,
    filterSubject,
    filterClass,
    filterType,
    sortBy,
    gradeFilter,
    sectionFilter,
    subjectPick,
    assignmentWord
  }), [allRubrics, filterMode, selectedDate, selectedEndDate, filterTeacher, filterSubject, filterClass, filterType, sortBy, gradeFilter, sectionFilter, subjectPick, assignmentWord]);

  const loadRubrics = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/rubrics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load rubrics');
      }
      const incoming = data.rubrics || [];
      setAllRubrics((prev) => {
        const cached = new Map((prev || []).filter((row) => row.bodyLoaded).map((row) => [row.id, row]));
        return incoming.map((row) => {
          const kept = cached.get(row.id);
          if (!kept) return row;
          return {
            ...kept,
            ...row,
            content: kept.content,
            issues_text: kept.issues_text,
            issues_json: kept.issues_json,
            regen_history: kept.regen_history,
            bodyLoaded: true
          };
        });
      });
    } catch (err) {
      console.error('Failed to load rubrics:', err);
    } finally {
      setRubricsLoaded(true);
    }
  };

  // The list rows omit the marking text. Opening, copying or printing one
  // rubric loads that text and keeps it on the card for the next click.
  const ensureRubricBody = async (rubric) => {
    if (!rubric) return null;
    if (rubric.bodyLoaded) return rubric;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/rubrics/${rubric.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load rubric');
      const full = {
        ...rubric,
        ...(data.rubric || {}),
        scan_count: rubric.scan_count,
        ocr_missing: rubric.ocr_missing,
        bodyLoaded: true
      };
      setAllRubrics((prev) => prev.map((row) => (
        row.id === full.id
          ? { ...row, ...full, scan_count: row.scan_count, ocr_missing: row.ocr_missing }
          : row
      )));
      return full;
    } catch (err) {
      console.error('Failed to load rubric:', err);
      setMessage({ show: true, text: err.message || 'Failed to load rubric', type: 'error' });
      return null;
    }
  };

  useEffect(() => {
    loadRubrics();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadRubrics();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Get unique teachers, subjects, and classes for the filter dropdowns.
  // Class list is sorted naturally ("1C" before "10C") so the dropdown order
  // tracks the way teachers refer to their classes.
  const uniqueTeachers = [...new Set(allRubrics.map(r => r.created_by))].sort();
  const uniqueSubjects = [...new Set(allRubrics.map(r => r.subject))].sort();
  const uniqueClasses = [...new Set(allRubrics.map(r => r.class).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    confirm.onConfirm(); 
    setConfirm({ show: false, text: '', onConfirm: null }); 
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const deleteRubric = async (id) => {
    showConfirm('Are you sure you want to delete this rubric?', async () => {

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/rubrics/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete rubric');
      }

      setMessage({ show: true, text: 'Rubric deleted successfully', type: 'success' });
      loadRubrics();
    } catch (err) {
      console.error('Error deleting rubric:', err);
      setMessage({ show: true, text: 'Failed to delete rubric: ' + err.message, type: 'error' });
    }
    });
  };

  const loadRubricForUse = async (rubricId) => {
    try {
      console.log('Setting rubric as active:', rubricId);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      const url = `${API_BASE_URL}/rubrics/${rubricId}/set-active`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      setMessage({ show: true, text: '✅ Rubric set as active! You can now use it in the Sheets tab.', type: 'success' });
      
      // Reload rubrics to show updated state if needed
      loadRubrics();
    } catch (err) {
      console.error('Error setting active rubric:', err);
      setMessage({ show: true, text: '❌ Failed to set active rubric: ' + err.message, type: 'error' });
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openModal = async (rubric) => {
    const full = await ensureRubricBody(rubric);
    if (!full) return;
    setModalRubric(full);
    setIsEditing(false);
    setEditedContent(stripQuestionPaperText(shownRubric(full.content)));
    setPickedQuestionIds({});
    setEditingBlockIndex(null);
    setQuestionDraft("");
    setModalRuleId("");
    setRuleSets([]);
    setRulesMenuOpen(false);
  };

  const closeModal = () => {
    setModalRubric(null);
    setEditInfoOpen(false);
    setIsEditing(false);
    setEditedContent("");
    setPickedQuestionIds({});
    setEditingBlockIndex(null);
    setQuestionDraft("");
    setRegenBusy(false);
    setRegenScope("");
    setRulesMenuOpen(false);
    setClassSwitchQuery("");
  };

  // The rules list on this window is the same set the generator uses.
  // Opening a rubric shows the generator's current choice, or the set whose
  // name matches this paper's subject when nothing is chosen yet.
  useEffect(() => {
    if (!modalRubric) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API_BASE_URL}/subject-rules`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const sets = data.data || [];
        setRuleSets(sets);
        setModalRuleId((current) => {
          if (current && sets.some((rs) => String(rs.id) === String(current))) return current;
          const picked = ruleSetForRubric(sets, modalRubric);
          return picked ? String(picked.id) : "";
        });
      } catch (_) { /* the menu stays empty and regenerate explains why */ }
    })();
    return () => { cancelled = true; };
  }, [modalRubric && modalRubric.id]);

  const startEditing = () => {
    setEditingBlockIndex(null);
    setQuestionDraft("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedContent(stripQuestionPaperText(shownRubric(modalRubric.content)));
  };

  const persistRubricContent = async (nextContent, successText, regenQuestions, regenMeta) => {
    const content = restorePaperNotes(nextContent, modalRubric.content);
    if (!String(content || "").trim()) {
      setMessage({ show: true, text: "Rubric content cannot be empty", type: "error" });
      return false;
    }
    const token = localStorage.getItem("authToken");
    const questions = Array.isArray(regenQuestions) ? regenQuestions.filter(Boolean) : [];
    const response = await fetch(`${API_BASE_URL}/rubrics/${modalRubric.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        class: modalRubric.class,
        subject: modalRubric.subject,
        content,
        issues_text: modalRubric.issues_text,
        issues_json: modalRubric.issues_json,
        pdf_name: modalRubric.pdf_name,
        ...(questions.length ? {
          regen_questions: questions,
          regen_model: (regenMeta && regenMeta.model) || "",
          regen_host: (regenMeta && regenMeta.host) || ""
        } : {})
      })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Failed to update rubric");
    const history = data.rubric && data.rubric.regen_history;
    const nextRubric = {
      ...modalRubric,
      content,
      ...(history ? { regen_history: history } : {})
    };
    setModalRubric(nextRubric);
    setAllRubrics((prev) => prev.map((row) => (
      row.id === modalRubric.id
        ? { ...row, content, regen_history: nextRubric.regen_history, bodyLoaded: true }
        : row
    )));
    loadRubrics();
    setMessage({ show: true, text: successText, type: "success" });
    return true;
  };

  const saveEdit = async () => {
    try {
      const ok = await persistRubricContent(editedContent, "Rubric updated successfully!");
      if (ok) setIsEditing(false);
    } catch (err) {
      console.error("Error updating rubric:", err);
      setMessage({ show: true, text: "Failed to update rubric: " + err.message, type: "error" });
    }
  };

  // Copy rubric content to clipboard (for EduPage)
  const copyForEduPage = (content) => {
    copyToClipboard(content).then(ok => {
      if (ok) {
        setMessage({ show: true, text: 'Rubric copied to clipboard for EduPage!', type: 'success' });
      } else {
        setMessage({ show: true, text: 'Failed to copy rubric', type: 'error' });
      }
    });
  };

  // The header Edit button saves through the same form used for a new rubric.
  const saveRubricInfo = async (formData) => {
    const token = localStorage.getItem('authToken');
    const body = {
      class: formData.class,
      subject: formData.subject,
      content: modalRubric.content,
      test_date: formData.validUntil || null,
      assignment_name: formData.assignmentName || '',
      paper_type: formData.paperType || '',
      type: formData.type || 'subjective',
      issues_text: modalRubric.issues_text,
      issues_json: modalRubric.issues_json
    };
    if (canManageAnyRubric(currentUser && currentUser.role)
      && (formData.createdByUsername || '') !== (modalRubric.created_by || '')) {
      body.created_by_username = formData.createdByUsername || '';
    }
    const response = await fetch(`${API_BASE_URL}/rubrics/${modalRubric.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.message || 'Failed to update rubric');
      setMessage({ show: true, text: err.message, type: 'error' });
      throw err;
    }
    const createdBy = body.created_by_username !== undefined
      ? (body.created_by_username || (currentUser && currentUser.username) || modalRubric.created_by)
      : modalRubric.created_by;
    setModalRubric({
      ...modalRubric,
      class: formData.class,
      subject: formData.subject,
      title: `${formData.class} - ${formData.subject}`,
      test_date: formData.validUntil || null,
      assignment_name: formData.assignmentName || null,
      paper_type: formData.paperType || null,
      type: formData.type || 'subjective',
      created_by: createdBy
    });
    setEditInfoOpen(false);
    loadRubrics();
    setMessage({ show: true, text: 'Rubric details updated.', type: 'success' });
  };

  // Edit PDF name
  const startEditingPdfName = () => {
    setIsEditingPdfName(true);
    setEditedPdfName(modalRubric.pdf_name || '');
  };

  const saveEditedPdfName = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/rubrics/${modalRubric.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class: modalRubric.class,
          subject: modalRubric.subject,
          content: modalRubric.content,
          issues_text: modalRubric.issues_text,
          issues_json: modalRubric.issues_json,
          pdf_name: editedPdfName
          // NOTE: test_date NOT included - date won't change when editing PDF name
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update PDF name');
      }

      setModalRubric({ ...modalRubric, pdf_name: editedPdfName });
      setIsEditingPdfName(false);
      loadRubrics();
      setMessage({ show: true, text: 'PDF name updated successfully!', type: 'success' });
    } catch (err) {
      setMessage({ show: true, text: 'Failed to update PDF name: ' + err.message, type: 'error' });
    }
  };

  // Edit rubric in Edit Rubrics page
  const editInEditPage = (content) => {
    localStorage.setItem("rubricPaperSource", content);
    localStorage.setItem("rubricText", stripQuestionPaperText(content));
    localStorage.removeItem("rubricPdfData");
    navigate('/rubrics/edit');
  };

  const canEdit = (rubric) => {
    return canManageAnyRubric(currentUser.role) ||
           currentUser.role === "teacher" ||
           rubric.created_by === currentUser.username;
  };

  // A teacher rewrites questions on a rubric they created. An examiner can
  // rewrite any rubric. The save call uses the same rule.
  const canReviseQuestions = (rubric) => {
    if (!currentUser || !rubric) return false;
    if (currentUser.role === "examiner") return true;
    if (currentUser.role === "teacher") return rubric.created_by === currentUser.username;
    return false;
  };

  const togglePickedQuestion = (id) => {
    setPickedQuestionIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const saveQuestionEdit = async () => {
    if (editingBlockIndex == null || !modalRubric) return;
    const base = shownRubric(modalRubric.content);
    const original = splitRubricQuestions(base).blocks[editingBlockIndex];
    if (!original) return;
    const replacement = restorePaperNotes(questionDraft, original.text);
    const next = shownRubric(replaceRubricBlockAt(base, editingBlockIndex, replacement));
    try {
      const ok = await persistRubricContent(next, "Question saved.");
      if (ok) {
        setEditingBlockIndex(null);
        setQuestionDraft("");
      }
    } catch (err) {
      setMessage({ show: true, text: "Failed to save question: " + err.message, type: "error" });
    }
  };

  const chooseRuleSet = (idStr) => {
    const next = modalRuleId === idStr ? "" : idStr;
    setModalRuleId(next);
    try { localStorage.setItem("rubricActiveRuleId", next); } catch (_) { /* the choice still applies to this window */ }
    setRulesMenuOpen(false);
  };

  const regenerateQuestions = async (chosen, scope) => {
    if (!modalRubric || regenBusy || editingBlockIndex != null) return;
    if (!chosen.length) return;
    const base = shownRubric(modalRubric.content);
    const ruleSet = ruleSets.find((rs) => String(rs.id) === String(modalRuleId));
    const rows = ruleRows(ruleSet);
    if (!ruleSet || !rows.length) {
      setMessage({
        show: true,
        text: "Select a Subject Rules set before regenerating.",
        type: "error"
      });
      return;
    }
    setRegenBusy(true);
    setRegenScope(scope);
    try {
      const token = localStorage.getItem("authToken");
      let rubricModel = "";
      let callHost = "";
      if (currentUser.role === "examiner") {
        rubricModel = rubricModelChoice;
        if (String(rubricModelChoice).startsWith("local:")) callHost = rubricHost;
      } else if (currentUser.role === "teacher") {
        const modelRes = await fetch(`${API_BASE_URL}/ai/teacher-rubric-model`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (modelRes.ok) {
          const modelData = await modelRes.json();
          rubricModel = modelData.model || "";
          if (String(rubricModel).startsWith("local:") && (modelData.host === "gpu" || modelData.host === "mac")) {
            callHost = modelData.host;
          }
        }
      }
      const prompt = `Rewrite ONLY these rubric questions: ${chosen.map((b) => b.display).join(", ")}.
Return one block per listed question and nothing else. Do not include any other question.
Keep each question's label and its "(N marks)" total exactly as they are now.
Write maths in plain text. No LaTeX and no dollar signs. Write 8 ≥ 5 and 4 < 5, not $8 \\ge 5$. Stack an addition or subtraction: the first number, then + or − and the second number, then a line of dashes, then the result. Long division is the bracket stack (12 ) 2345, quotient above, each subtraction on its own line). Never the one-line crossed method with Bring down arrows.

Each block MUST use this shape:

Q.<label> - <question statement> (<N> marks)


Model Answer: <the teacher's answer in plain text>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <step> | <marks> |

The Marks column of each block must add up to that question's "(N marks)".
The question statement must include the number, value, or expression the student uses, so the paper and the rubric both show it. Write "Write the number 3417 in words" and "Solve 7676 − 3434", not a bare "Write the number in words" or "Solve". Use the value already in the question. Do not invent a different one. A long passage stays out of the statement. Copy the teacher's question wording. Add a number or expression only when the student must work on that given, as in "Solve 7676 − 3434". A recalled name, source, example, or fact stays in the Model Answer. Rejected: "دو ذرائع حفظ کرنا اور کتابت لکھیں". Required: "کوئی سے دو ذرائع لکھیں", with حفظ کرنا and کتابت only in the Model Answer.
When the question asks for one fixed word or form (plural, singular, opposite, gender, degree, verb form, contraction, a spelling word, a one-word blank; جمع، واحد، متضاد، مذکر، مؤنث، مصدر), the Sub-step cell is that word, not a description. Model Answer is the same word. Write "| Knives | 1 |", not "| Correct plural form written | 1 |". Sentences, translations, and paragraphs stay descriptive rows.
Each breakdown row is working the student must write. Do not award a row for copying a number already in the question. An intermediate value is a row only inside the correct method: "Substitutes P = 2(200 + 150)" and "Multiplies 2 × 350 = 700", not "Identifies length as 200 cm" and not a standalone "Adds 200 + 150 = 350 correctly".
${CLOSED_WORLD_BREAKDOWN}

Current version to replace:
${chosen.map((b) => b.text).join("\n\n")}

Answer key:
${base}
`;
      const rulesName = ruleSet.subject_key || ruleSet.display_name || "";
      const questionLabels = chosen.map((b) => b.display).filter(Boolean);
      const reply = await chatWithRubricAI(prompt, "", {
        subjectName: rulesName || modalRubric.subject || "",
        subjectRules: rows,
        subjectInstructions: ruleSet.instructions || "",
        mode: "rubric",
        rubricModel,
        rubricHost: callHost,
        logAbout: folderDisplayTitle(modalRubric),
        logHint: [rulesName, ...questionLabels].filter(Boolean).join("\n")
      });
      const raw = reply && reply.content != null ? reply.content : reply;
      if (typeof raw === "string" && raw.startsWith("Error:")) {
        throw new Error(raw.replace(/^Error:\s*/, "") || "Could not regenerate those questions.");
      }
      const fresh = splitRubricQuestions(shownRubric(raw));
      const map = new Map();
      fresh.blocks.forEach((b) => map.set(b.id, b.text));
      if ([...map.keys()].length === 0 && fresh.blocks.length === chosen.length) {
        chosen.forEach((b, i) => map.set(b.id, fresh.blocks[i].text));
      } else {
        chosen.forEach((b, i) => {
          if (!map.has(b.id) && fresh.blocks.length === chosen.length && fresh.blocks[i]) {
            map.set(b.id, fresh.blocks[i].text);
          }
        });
      }
      if (map.size === 0) throw new Error("The new reply did not include the selected questions.");
      const stillMissing = chosen.filter((b) => !map.has(b.id));
      const regenerated = chosen.filter((b) => map.has(b.id));
      const next = replaceRubricBlocks(base, map);
      const note = stillMissing.length
        ? `Regenerated ${map.size} question${map.size === 1 ? "" : "s"}. ${stillMissing.map((b) => b.display).join(", ")} stayed as they were.`
        : `Regenerated ${map.size} question${map.size === 1 ? "" : "s"}.`;
      const rawModel = String(rubricModel || "");
      const modelId = rawModel.includes(":") ? rawModel.slice(rawModel.indexOf(":") + 1) : rawModel;
      const ok = await persistRubricContent(next, note, regenerated.map((b) => b.display), {
        model: modelId,
        host: callHost
      });
      if (ok && scope === "selected") setPickedQuestionIds({});
    } catch (err) {
      setMessage({ show: true, text: err.message || "Could not regenerate those questions.", type: "error" });
    } finally {
      setRegenBusy(false);
      setRegenScope("");
    }
  };

  const regenerateSelected = () => {
    if (!modalRubric) return;
    const parts = splitRubricQuestions(shownRubric(modalRubric.content));
    const chosen = parts.blocks.filter((b) => pickedQuestionIds[b.id]);
    regenerateQuestions(chosen, "selected");
  };

  const regenerateAll = () => {
    if (!modalRubric || regenBusy || editingBlockIndex != null) return;
    const parts = splitRubricQuestions(shownRubric(modalRubric.content));
    if (!parts.blocks.length) return;
    const ruleSet = ruleSets.find((rs) => String(rs.id) === String(modalRuleId));
    const name = ruleSet ? ruleSet.display_name : "the selected";
    showConfirm(
      `Regenerate all ${parts.blocks.length} questions with ${name} rules?`,
      () => regenerateQuestions(parts.blocks, "all")
    );
  };

  const reviseOpen = !!(modalRubric && canReviseQuestions(modalRubric));
  const modalParts = reviseOpen
    ? splitRubricQuestions(shownRubric(modalRubric.content))
    : { preamble: "", blocks: [] };
  const rubricRuns = rubricContentRuns(modalRubric);
  const severalRubrics = rubricRuns.length > 1;
  const pickedCount = modalParts.blocks.filter((b) => pickedQuestionIds[b.id]).length;
  const modalRuleSet = modalRuleId
    ? (ruleSets.find((rs) => String(rs.id) === String(modalRuleId)) || null)
    : null;
  const rulesReady = !!(modalRuleSet && ruleRows(modalRuleSet).length);
  const regenLocked = regenBusy || isEditing || editingBlockIndex != null;
  const rulesViewer = currentUser && ["administrator", "examiner", "management"].includes(currentUser.role);
  const renderQuestionCheck = (b, editingThis) => (
    <label className="rc-qblock__check" title={`Select ${b.display}`}>
      <input
        type="checkbox"
        checked={!!pickedQuestionIds[b.id]}
        disabled={regenBusy || editingThis}
        onChange={() => togglePickedQuestion(b.id)}
        aria-label={`Select ${b.display}`}
      />
    </label>
  );
  const renderQuestionBlock = (b, bi, isFirst) => {
    const editingThis = editingBlockIndex === bi;
    const parts = splitQuestionStatement(shownRubric(b.text));
    return (
      <div key={`${b.id}-${bi}`} className={`rc-qblock${isFirst ? " is-first" : ""}${pickedQuestionIds[b.id] ? " is-on" : ""}${editingThis ? " is-editing" : ""}`}>
        <div className="rc-qblock__body">
          {editingThis ? (
            <div className="rc-qstatement">
              {renderQuestionCheck(b, true)}
              <div className="rc-qstatement__text">
                <textarea
                  className="rc-qedit__box"
                  value={questionDraft}
                  onChange={(e) => setQuestionDraft(e.target.value)}
                  autoFocus
                  spellCheck={false}
                  aria-label={`Edit ${b.display}`}
                />
                <div className="rc-qedit__actions">
                  <button type="button" className="rc-btn rc-btn--success" onClick={saveQuestionEdit} disabled={regenBusy}>
                    <i className="fas fa-check"></i> Save question
                  </button>
                  <button
                    type="button"
                    className="rc-btn rc-btn--muted"
                    onClick={() => { setEditingBlockIndex(null); setQuestionDraft(""); }}
                    disabled={regenBusy}
                  >
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="rc-qstatement">
                {renderQuestionCheck(b, false)}
                <div className="rc-qstatement__text">
                  <RubricMarkdown>{parts.statement || b.display}</RubricMarkdown>
                </div>
                <button
                  type="button"
                  className="rc-qedit"
                  disabled={regenBusy}
                  onClick={() => {
                    setEditingBlockIndex(bi);
                    setQuestionDraft(stripQuestionPaperText(b.text));
                  }}
                  title={`Edit ${b.display}`}
                >
                  <i className="fas fa-pen"></i> Edit
                </button>
              </div>
              {parts.rest ? <RubricMarkdown>{parts.rest}</RubricMarkdown> : null}
            </>
          )}
        </div>
      </div>
    );
  };
  const renderCurrentRubric = () => (
    reviseOpen && modalParts.blocks.length > 0 ? (
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-primary)" }}>
        {modalParts.preamble.trim() ? (
          <RubricMarkdown>{modalParts.preamble}</RubricMarkdown>
        ) : null}
        {modalParts.blocks.map((b, bi) => renderQuestionBlock(b, bi, bi === 0))}
      </div>
    ) : (
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-primary)" }}>
        <RubricMarkdown>{shownRubric(modalRubric.content)}</RubricMarkdown>
      </div>
    )
  );

  if (rubricsLoaded && savedRubrics.length === 0 && allRubrics.length === 0 && filterMode !== "none") {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        <h2 style={{ fontSize: '1.125rem' }}>Saved Rubrics</h2>
        <p style={{ color: "#666", marginTop: 12, fontSize: '0.8125rem' }}>
          {filterMode === "today"
            ? "No rubrics with today's date found."
            : filterMode === "future"
            ? "No upcoming rubrics (dated tomorrow onwards)."
            : filterMode === "date" && selectedDate
            ? (selectedEndDate
              ? `No rubrics found for ${selectedDate} – ${selectedEndDate}.`
              : `No rubrics found for ${selectedDate}.`)
            : "No saved rubrics yet. Generate and save rubrics from the Rubric or Rubric Gen tabs."
          }
        </p>
        {(filterMode === "today" || filterMode === "future" || (filterMode === "date" && selectedDate)) && (
          <button
            onClick={() => { setFilterMode("all"); setSelectedDate(""); setSelectedEndDate(""); }}
            style={{
              marginTop: 12,
              padding: "8px 18px",
              fontSize: 13,
              borderRadius: 6,
              border: "none",
              background: "linear-gradient(135deg, #3B82F6, #2563EB)",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #2563EB, #1D4ED8)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #3B82F6, #2563EB)";
            }}
          >
            ← View All Rubrics
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 0.5rem', maxWidth: '100%', margin: '0 auto', background: 'var(--surface, #ffffff)' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
          }}>
            <i className="fas fa-bookmark" style={{ color: 'white', fontSize: '20px' }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              color: 'var(--text-primary)', 
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: '700'
            }}>Saved Rubrics</h1>
            <p style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: '0.75rem'
            }}>Browse and manage your saved grading rubrics</p>
          </div>
          {/* Future Cards — top-row toggle: shows every rubric dated tomorrow
              onwards, regardless of the calendar's selected day. */}
          <button
            onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode(filterMode === 'future' ? 'none' : 'future'); setSelectedDate(''); setSelectedEndDate(''); }}
            title="Show only rubrics dated tomorrow onwards"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              background: filterMode === 'future' ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'var(--surface)',
              color: filterMode === 'future' ? 'white' : 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-forward"></i>
            {filterMode === 'future' ? 'Future Cards · On' : 'Future Cards'}
            {futureRubricCount > 0 && (
              <span style={{
                minWidth: 16, height: 16, padding: '0 5px', borderRadius: 8,
                fontSize: 10, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: filterMode === 'future' ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                color: 'white'
              }}>{futureRubricCount}</span>
            )}
          </button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Teacher Filter (moved from below; replaces All / Today toggle) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-user-tie" style={{ color: '#8B5CF6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
              <select
                value={filterTeacher}
                onChange={(e) => setFilterTeacher(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                  minWidth: 150,
                  background: 'var(--surface)'
                }}
              >
                <option value="">All Teachers</option>
                {uniqueTeachers.map(teacher => (
                  <option key={teacher} value={teacher}>{teacher}</option>
                ))}
              </select>
            </div>

            {/* Class Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
              <MultiCheckSelect
                value={filterClass}
                onChange={setFilterClass}
                options={uniqueClasses}
                allLabel="All Classes"
                minWidth={140}
              />
            </div>

            {/* Subject Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-book" style={{ color: '#10b981', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
              <MultiCheckSelect
                value={filterSubject}
                onChange={setFilterSubject}
                options={uniqueSubjects}
                allLabel="All Subjects"
                minWidth={160}
              />
            </div>

            {/* All Rubrics / Today toggle — mirrors the same shortcut on
                ScansTab. "All" clears any date selection so the rubric grid
                shows everything; "Today" filters to rubrics dated today. */}
            <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', background: 'var(--tab-bg, rgba(0,0,0,0.04))', padding: 3, borderRadius: 8 }}>
              <button
                onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode('all'); setSelectedDate(''); setSelectedEndDate(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: filterMode === 'all' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: filterMode === 'all' ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                All
              </button>
              <button
                onClick={() => { setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setFilterMode('today'); setSelectedDate(''); setSelectedEndDate(''); }}
                style={{
                  padding: '5px 12px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  background: filterMode === 'today' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                  color: filterMode === 'today' ? 'white' : 'var(--text-primary)',
                  transition: 'all 0.2s'
                }}
              >
                Today
              </button>
            </div>

            {(filterTeacher || filterSubject.length > 0 || filterClass.length > 0 || filterType !== 'subjective' || gradeFilter || assignmentWord) && (
              <button
                onClick={() => { setFilterTeacher(''); setFilterSubject([]); setFilterClass([]); setFilterType('subjective'); setGradeFilter(null); setSectionFilter(null); setSubjectPick(null); setAssignmentWord(null); }}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)'
                }}
              >
                <i className="fas fa-filter-circle-xmark"></i>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Two-column layout: compact 3-column calendar on the left, rubric
          cards on the right. Wraps to a single column on narrow viewports. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ width: 360, flexShrink: 0 }}>
          <DateFilterCalendar
            items={visibleRubrics}
            dateField="test_date"
            range
            selectedDate={filterMode === 'date' ? selectedDate : ''}
            selectedEndDate={filterMode === 'date' ? selectedEndDate : ''}
            onDateChange={(startStr, endStr = '') => {
              setGradeFilter(null);
              setSectionFilter(null);
              setSubjectPick(null);
              if (startStr) {
                setSelectedDate(startStr);
                setSelectedEndDate(endStr || '');
                setFilterMode('date');
              } else {
                setSelectedDate('');
                setSelectedEndDate('');
                setFilterMode('none');
              }
            }}
            itemLabelSingular="Rubric"
            itemLabelPlural="Rubrics"
            title="Date range"
            columns={5}
            // Show the next-30-days window while "Future Cards" is on, and keep
            // it while a future day stays selected (so the picked range remains
            // visible instead of the strip snapping back to today).
            future={filterMode === 'future' || (filterMode === 'date' && !!selectedDate && (
              selectedDate > todayKey || (!!selectedEndDate && selectedEndDate > todayKey)
            ))}
          />

          {/* Rubric-type segmented filter — pinned to the left rail under
              the calendar. "MCQs" keeps only rubrics with type === 'mcq';
              "Subjective" keeps the rest. Same visual treatment as the
              Sorted/Status filters on AnalyzeTab so the two tabs feel
              consistent. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', marginTop: 8 }}>
            <div style={{
              display: 'inline-flex',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--surface)',
              flex: '1 1 0',
              minWidth: 0
            }}>
              {[
                { v: 'all',         label: 'All' },
                { v: 'mcq',         label: 'MCQs' },
                { v: 'subjective',  label: 'Subjective' }
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setFilterType(opt.v)}
                  style={{
                    flex: 1,
                    padding: '7px 2px',
                    border: 'none',
                    background: filterType === opt.v
                      ? 'linear-gradient(135deg,#8B5CF6,#6D28D9)'
                      : 'transparent',
                    color: filterType === opt.v ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sort-by segmented control — sits under the type filter so
              the teacher can re-order the rubric grid by date / teacher /
              class. Date = newest first (test_date desc), Teacher =
              alpha on created_by, Class = natural on class. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', marginTop: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 2 }}>
              Sort:
            </span>
            <div style={{
              display: 'inline-flex',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--surface)',
              flex: '1 1 0',
              minWidth: 0
            }}>
              {[
                { v: 'date',    label: 'Date' },
                { v: 'teacher', label: 'Teacher' },
                { v: 'class',   label: 'Class' }
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setSortBy(opt.v)}
                  style={{
                    flex: 1,
                    padding: '6px 4px',
                    border: 'none',
                    background: sortBy === opt.v
                      ? 'linear-gradient(135deg,#F59E0B,#D97706)'
                      : 'transparent',
                    color: sortBy === opt.v ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 280, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ClassSectionFilters
            items={visibleRubrics}
            itemNoun="rubrics"
            gradeFilter={gradeFilter}
            sectionFilter={sectionFilter}
            subjectFilter={subjectPick}
            assignmentWord={assignmentWord}
            onAssignmentWord={setAssignmentWord}
            onGrade={(grade) => {
              setSubjectPick(null);
              setSectionFilter(null);
              if (gradeFilter === grade) {
                setGradeFilter(null);
              } else {
                setGradeFilter(grade);
                setSelectedDate('');
                setSelectedEndDate('');
                setFilterMode('none');
              }
            }}
            onSection={(section) => {
              const next = sectionFilter === section ? null : section;
              setSectionFilter(next);
              if (subjectPick && !itemsInClass(visibleRubrics, gradeFilter, next).some((rubric) => (rubric.subject || '').trim() === subjectPick)) {
                setSubjectPick(null);
              }
            }}
          />
        {(filterMode !== "none" || gradeFilter) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 5,
          }}
        >
          {savedRubrics.length === 0 && (
            <p style={{ color: "#666", fontSize: "0.8125rem", margin: 0 }}>{gradeFilter ? 'No rubrics for this class.' : 'No rubrics for this selection.'}</p>
          )}
          {savedRubrics.map((rubric) => {
          const isUsed = rubric.status === 'used';
          const isPrinted = !!rubric.printed_at;
          const isDone = !isPrinted && !!rubric.completed_at;
          const scanCount = Number(rubric.scan_count || 0);
          // Empty (0 images) takes top visual priority — same rule as
          // AnalyzeTab so the folder reads identically across tabs.
          const isEmpty = scanCount === 0;
          const cardBackground = isEmpty
            ? 'rgba(239, 68, 68, 0.10)'
            : isPrinted
              ? 'rgba(33, 150, 243, 0.10)'
              : isDone
                ? 'rgba(40, 167, 69, 0.10)'
                : isUsed
                  ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.02))'
                  : 'var(--surface, #ffffff)';
          const cardBorder = isEmpty
            ? '3px solid #EF4444'
            : isPrinted
              ? '3px solid #2196F3'
              : isDone
                ? '3px solid #28a745'
                : isUsed
                  ? '3px solid #9E9E9E'
                  : '3px solid #F59E0B';
          // Keep the grayed-out look only for plain "used" rubrics — once a
          // rubric is Printed/Done/Empty we want the status color to read clearly.
          const cardOpacity = (isUsed && !isPrinted && !isDone && !isEmpty) ? 0.7 : 1;
          const cardFilter = (isUsed && !isPrinted && !isDone && !isEmpty) ? 'grayscale(30%)' : 'none';
          const baseShadow = (isUsed && !isPrinted && !isDone && !isEmpty)
            ? '0 2px 4px rgba(0,0,0,0.1)'
            : '0 4px 12px rgba(0,0,0,0.08)';
          return (
          <div
            key={rubric.id}
            style={{
              border: cardBorder,
              borderRadius: 12,
              padding: 18,
              background: cardBackground,
              boxShadow: baseShadow,
              display: 'flex',
              flexDirection: 'column',
              opacity: cardOpacity,
              filter: cardFilter,
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isUsed || isPrinted || isDone) {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = baseShadow;
            }}
          >
            {/* Status Badge — Empty takes priority over Printed/Done/Used,
                same as AnalyzeTab so the folder reads consistently. */}
            {isEmpty && (
              <div style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#EF4444",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 2px 4px rgba(239,68,68,0.3)"
              }}>
                ⚠ Empty
              </div>
            )}
            {!isEmpty && isPrinted && (
              <div style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#2196F3",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 2px 4px rgba(33,150,243,0.3)"
              }}>
                🖨 Printed
              </div>
            )}
            {!isEmpty && isDone && (
              <div style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#2563eb",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 4px rgba(37,99,235,0.3)"
              }}>
                🖨 Ready to Print
              </div>
            )}
            {!isEmpty && isUsed && !isPrinted && !isDone && (
              <div style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#757575",
                color: "white",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                ✓ Used
              </div>
            )}

            {/* Header */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span>
                  {(() => {
                    const dateStr = rubric.test_date
                      ? new Date(rubric.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : '';
                    return rubricNameWithDate(rubric, dateStr);
                  })()}
                </span>
                {/* Test-type chip — same-class-same-date rubrics can now coexist
                    as Subjective and MCQs, so the chip is the only visual
                    distinguisher on the card. */}
                {(() => {
                  const isMcq = (rubric.type || 'subjective') === 'mcq';
                  return (
                    <span
                      title={isMcq ? 'MCQs / OMR bubble-sheet rubric' : 'Subjective / handwritten answer-sheet rubric'}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        background: isMcq ? '#ede9fe' : '#e0f2fe',
                        color: isMcq ? '#6d28d9' : '#0369a1',
                        border: `1px solid ${isMcq ? '#c4b5fd' : '#bae6fd'}`,
                      }}
                    >
                      {isMcq ? 'MCQs' : 'Subjective'}
                    </span>
                  );
                })()}
              </div>
              {/* Assignment name — shown right under the title (same tag
                  styling as the Sort-tab folder cards) when the rubric has
                  one set. */}
              {rubric.assignment_name && (
                <div
                  title={`Assignment: ${rubric.assignment_name}`}
                  style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, minWidth: 0 }}
                >
                  <i className="fa-solid fa-tag" style={{ width: 12, color: '#2563EB', flexShrink: 0 }}></i>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rubric.assignment_name}
                  </span>
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
                Created by: {rubric.created_by}
              </div>
              {/* Image count + date/time + status sit on ONE row so the
                  card stays compact. Status renders only when the rubric
                  actually has one (printed / done / pending — used rubrics
                  with no status show nothing here). */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                <span
                  title={`${scanCount} image${scanCount !== 1 ? 's' : ''} in this folder`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 700,
                    background: isEmpty ? '#ffebee' : '#e8f5e9',
                    color: isEmpty ? '#c62828' : '#2e7d32',
                    border: `1px solid ${isEmpty ? '#ef9a9a' : '#a5d6a7'}`,
                  }}
                >
                  <i className="fas fa-image" style={{ marginRight: 4 }}></i>
                  {scanCount} image{scanCount !== 1 ? 's' : ''}
                </span>
                {Array.isArray(rubric.ocr_missing) && rubric.ocr_missing.length > 0 && (
                  <span
                    title={ocrMissingFolderTitle(rubric.ocr_missing)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 700,
                      background: '#fff7ed',
                      color: '#c2410c',
                      border: '1px solid #fdba74',
                      cursor: 'default'
                    }}
                  >
                    ocr missing
                  </span>
                )}
                <span style={{ fontSize: 11, color: "#666" }}>
                  {formatDate(rubric.created_at)}
                </span>
                {isPrinted ? (
                  <span style={{ fontSize: 10, color: "#2196F3", fontWeight: 700, textTransform: "uppercase" }}>
                    Status: Printed
                  </span>
                ) : isDone ? (
                  <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, textTransform: "uppercase" }}>
                    Status: Ready to Print
                  </span>
                ) : !isUsed ? (
                  <span style={{ fontSize: 10, color: "#4CAF50", fontWeight: 600, textTransform: "uppercase" }}>
                    Status: Pending
                  </span>
                ) : null}
              </div>
              {rubric.pdf_name && (
                <div style={{ fontSize: 11, color: "#2196F3", marginTop: 4, fontWeight: 500 }}>
                  PDF: {rubric.pdf_name}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              <button
                onClick={() => openModal(rubric)}
                style={{
                  padding: "8px 12px",
                  fontSize: 13,
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                }}
                title="Show"
              >
                <i class="fa-solid fa-eye"></i>
              </button>
              <button
                onClick={async () => {
                  const full = await ensureRubricBody(rubric);
                  if (!full) return;
                  copyForEduPage(stripQuestionPaperText(shownRubric(full.content)));
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: 13,
                  borderRadius: 6,
                  border: "none",
                  background: "#FF9800",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                title="Copy rubric"
              >
                <i class="fa-solid fa-copy"></i>
              </button>
              <button
                onClick={async () => {
                  const full = await ensureRubricBody(rubric);
                  if (!full) return;
                  setQuestionPaper(attachRubricCard(extractQuestionPaper(shownRubric(full.content)), full));
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: 13,
                  borderRadius: 6,
                  border: "none",
                  background: "#0f766e",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                title="Extract the questions only and print an exam paper"
              >
                <i className="fa-solid fa-print"></i>
              </button>
              {canEdit(rubric) && (
                <>
                  <button
                    onClick={() => openModal(rubric)}
                    style={{
                      padding: "8px 12px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "none",
                      background: "#2196F3",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#1976D2";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#2196F3";
                    }}
                    title="Edit rubric"
                    >
                    <i className="fas fa-pen"></i>
                  </button>
                  {(canManageAnyRubric(currentUser.role) || rubric.created_by === currentUser.username) && (
                    <button
                    onClick={() => deleteRubric(rubric.id)}
                    style={{
                      padding: "8px 12px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "none",
                      background: "#f44336",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#d32f2f";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f44336";
                    }}
                    title="Delete rubric"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                  {canManageAnyRubric(currentUser.role) && (
                    <button
                      onClick={() => loadRubricForUse(rubric.id)}
                      style={{
                        padding: "8px 12px",
                        fontSize: 13,
                        borderRadius: 6,
                        border: "none",
                        background: "#4caf50",
                        color: "white",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      title="Use rubric"
                    >
                      Use
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}
        </div>
        )}
        </div>
        <SubjectColumn
          items={visibleRubrics}
          gradeFilter={gradeFilter}
          sectionFilter={sectionFilter}
          subjectFilter={subjectPick}
          onSubject={(subject) => setSubjectPick(subjectPick === subject ? null : subject)}
        />
        </div>
      </div>

      {/* Full-screen Modal */}
      {modalRubric && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "stretch",
            justifyContent: "stretch",
            padding: 0,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: "var(--panel-bg, #ffffff)",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              maxWidth: "none",
              maxHeight: "none",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Compact Layout */}
            <div
              style={{
                padding: "12px 20px",
                borderBottom: "1px solid var(--border-color)",
                position: "relative",
                zIndex: 3,
                flex: "0 0 auto",
              }}
            >
              {/* Row 1: Title, Created by, Date - all in one line */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", minWidth: 0 }}>
                  <div style={{ width: 260, flex: "0 0 auto" }} title="Type the start of a class, such as 10, to list only those classes">
                    <Select
                      aria-label="Load another class"
                      value={null}
                      inputValue={classSwitchQuery}
                      onInputChange={(val, meta) => {
                        if (meta.action === "input-change") setClassSwitchQuery(val);
                        if (meta.action === "menu-close" || meta.action === "set-value") setClassSwitchQuery("");
                      }}
                      onChange={(option) => {
                        const next = allRubrics.find((row) => String(row.id) === String(option && option.value));
                        setClassSwitchQuery("");
                        if (next) openModal(next);
                      }}
                      options={allRubrics
                        .filter((row) => !modalRubric || String(row.id) !== String(modalRubric.id))
                        .map((row) => {
                          const dateStr = row.test_date
                            ? new Date(row.test_date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                            : "";
                          const classLabel = rubricNameWithDate(row, dateStr) || row.title || `Rubric #${row.id}`;
                          const assignment = String(row.assignment_name || "").trim();
                          return {
                            value: row.id,
                            label: assignment ? `${classLabel} — ${assignment}` : classLabel
                          };
                        })
                        .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: "base" }))}
                      filterOption={(option, raw) => {
                        const q = String(raw || "").trim().toLowerCase();
                        if (!q) return true;
                        return String(option.label || "").trim().toLowerCase().startsWith(q);
                      }}
                      placeholder="Load another class…"
                      isSearchable
                      openMenuOnFocus
                      isDisabled={regenBusy}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      noOptionsMessage={({ inputValue }) => (
                        inputValue ? `No class starts with “${inputValue}”` : "No other classes"
                      )}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 30,
                          borderColor: "#6EE7B7",
                          borderRadius: 6,
                          background: "rgba(255,255,255,0.85)",
                          boxShadow: "none",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "text"
                        }),
                        valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                        input: (base) => ({ ...base, margin: 0, padding: 0, color: "#065F46", fontWeight: 700 }),
                        placeholder: (base) => ({ ...base, color: "#065F46", fontWeight: 700, fontSize: 12 }),
                        indicatorsContainer: (base) => ({ ...base, height: 28 }),
                        dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#065F46" }),
                        indicatorSeparator: () => ({ display: "none" }),
                        menuPortal: (base) => ({ ...base, zIndex: 11000 }),
                        menu: (base) => ({ ...base, fontSize: 12, fontWeight: 700, zIndex: 11000 }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          color: state.isFocused || state.isSelected ? "#fff" : "#065F46",
                          background: state.isFocused || state.isSelected ? "#2563EB" : "#fff"
                        })
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>
                    {folderDisplayTitle(modalRubric)}
                  </span>
                  <span style={{ fontSize: 13, color: "#666" }}>
                    Created by: <strong>{modalRubric.created_by}</strong>
                  </span>
                  <span style={{ fontSize: 12, color: "#888" }}>
                    {formatDate(modalRubric.created_at)}
                  </span>
                  {regenHistoryOf(modalRubric).filter((entry) => !String(entry.content || "").trim()).map((entry, i) => (
                    <RegenTimeChip key={`${entry.at}-${i}`} entry={entry} label={formatDate(entry.at)} />
                  ))}
                </div>
                <button
                  onClick={closeModal}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 24,
                    cursor: "pointer",
                    color: "var(--text-primary)",
                    padding: "0 4px",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Row 2: Class, Subject, Test Date, PDF - all in one row */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
                {canEdit(modalRubric) && (
                  <button
                    onClick={() => setEditInfoOpen(true)}
                    title="Edit class, subject, papers, assignment, test type, and date"
                    style={{ padding: "2px 8px", fontSize: 11, border: "none", borderRadius: 4, background: "#2196F3", color: "white", cursor: "pointer", fontWeight: 700 }}
                  >
                    Edit
                  </button>
                )}
                <span>Class: <strong>{modalRubric.class}</strong></span>
                <span style={{ color: "#ddd" }}>|</span>
                <span>Subject: <strong>{modalRubric.subject}</strong></span>
                <span style={{ color: "#ddd" }}>|</span>
                <span style={{ color: "#ff9800" }}>Test date: <strong>{modalRubric.test_date ? new Date(modalRubric.test_date).toLocaleDateString() : 'Not set'}</strong></span>
                <span style={{ color: "#ddd" }}>|</span>
                <span>Papers: <strong>{modalRubric.paper_type || 'Not set'}</strong></span>
                <span style={{ color: "#ddd" }}>|</span>
                <span>Type: <strong>{modalRubric.type === 'mcq' ? 'MCQs' : 'Subjective'}</strong></span>
                <span style={{ color: "#ddd" }}>|</span>
                
                {/* PDF Name */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#2196F3" }}>
                  {isEditingPdfName ? (
                    <>
                      <span style={{ color: "var(--text-primary)" }}>PDF:</span>
                      <input
                        type="text"
                        value={editedPdfName}
                        onChange={(e) => setEditedPdfName(e.target.value)}
                        placeholder="filename.pdf"
                        style={{ padding: "3px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12, width: 120 }}
                      />
                      <button onClick={saveEditedPdfName} style={{ padding: "3px 6px", fontSize: 11, border: "none", borderRadius: 4, background: "#4caf50", color: "white", cursor: "pointer" }}>Save</button>
                      <button onClick={() => setIsEditingPdfName(false)} style={{ padding: "3px 6px", fontSize: 11, border: "1px solid #ddd", borderRadius: 4, background: "white", cursor: "pointer" }}>✕</button>
                    </>
                  ) : (
                    <>
                      <span>PDF: <strong>{modalRubric.pdf_name || 'Not set'}</strong></span>
                      {canEdit(modalRubric) && (
                        <button onClick={startEditingPdfName} style={{ padding: "2px 6px", fontSize: 11, border: "none", borderRadius: 4, background: "#2196F3", color: "white", cursor: "pointer" }}>Edit PDF</button>
                      )}
                    </>
                  )}
                </div>

                <span style={{ color: "#ddd" }}>|</span>
                <span style={{ color: "#9c27b0" }}>Assignment: <strong>{modalRubric.assignment_name || 'Not set'}</strong></span>
              </div>
              {reviseOpen && modalParts.blocks.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => setRulesMenuOpen((open) => !open)}
                      title="Choose the Subject Rules used when regenerating"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px",
                        border: "1px solid var(--border-color, #d1d5db)",
                        borderRadius: 6,
                        background: rulesMenuOpen ? "rgba(59, 130, 246, 0.12)" : "var(--panel-bg, #fff)",
                        color: "var(--text-primary)",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        whiteSpace: "nowrap"
                      }}
                    >
                      <i className="fas fa-list-check"></i>
                      {modalRuleSet ? modalRuleSet.display_name : "Subject Rules"}
                      <i className={`fas fa-chevron-${rulesMenuOpen ? "up" : "down"}`} style={{ fontSize: 10 }}></i>
                    </button>
                    {rulesMenuOpen && (
                      <>
                        <div
                          onClick={() => setRulesMenuOpen(false)}
                          style={{ position: "fixed", inset: 0, zIndex: 30 }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            left: 0,
                            zIndex: 31,
                            width: 360,
                            maxWidth: "70vw",
                            maxHeight: 320,
                            overflow: "auto",
                            padding: 8,
                            borderRadius: 10,
                            border: "1px solid var(--border-color, #e5e7eb)",
                            background: "var(--panel-bg, #fff)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)"
                          }}
                        >
                          <div className="rc-rules-list" style={{ width: "100%" }}>
                            {ruleSets.length === 0 && (
                              <div style={{ fontSize: 12, color: "#666", padding: "6px 4px" }}>No subject rules yet.</div>
                            )}
                            {ruleSets.map((rs) => {
                              const idStr = String(rs.id);
                              const isActive = modalRuleId === idStr;
                              const showCreator = rulesViewer && !rs.is_default && rs.created_by && rs.created_by !== (currentUser && currentUser.username);
                              return (
                                <div
                                  key={idStr}
                                  onClick={() => chooseRuleSet(idStr)}
                                  title={isActive ? "Click to clear" : "Click to apply these rules"}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      chooseRuleSet(idStr);
                                    }
                                  }}
                                  className="rc-rule-pill"
                                  style={{
                                    background: isActive
                                      ? "linear-gradient(135deg, #3B82F6, #2563EB)"
                                      : "var(--rule-pill-bg, rgba(59, 130, 246, 0.15))",
                                    color: isActive ? "white" : "var(--rule-pill-text, #1E40AF)",
                                    boxShadow: isActive ? "0 2px 8px rgba(59,130,246,0.3)" : "none"
                                  }}
                                >
                                  {isActive && <i className="fa-solid fa-check"></i>}
                                  <span className="rc-rule-pill__label">{rs.display_name}</span>
                                  {rs.is_default && (
                                    <span
                                      className="rc-rule-pill__badge"
                                      title="Admin default — read only"
                                      style={{
                                        fontWeight: 700,
                                        background: isActive ? "rgba(255,255,255,0.25)" : "var(--rule-admin-bg, rgba(245, 158, 11, 0.2))",
                                        color: isActive ? "white" : "var(--rule-admin-text, #B45309)"
                                      }}
                                    >
                                      ADMIN
                                    </span>
                                  )}
                                  {showCreator && (
                                    <span
                                      className="rc-rule-pill__badge"
                                      title={`Created by ${rs.created_by}`}
                                      style={{
                                        fontWeight: 600,
                                        background: isActive ? "rgba(255,255,255,0.25)" : "var(--rule-creator-bg, rgba(0,0,0,0.08))",
                                        color: isActive ? "white" : "var(--rule-creator-text, #374151)"
                                      }}
                                    >
                                      by {rs.created_by}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {modalRuleSet && (
                    <div className="rc-rules-applied" style={{ width: "auto", minWidth: 0, marginTop: 0 }}>
                      <i className="fa-solid fa-circle-check" style={{ marginRight: 4 }}></i>
                      {modalRuleSet.display_name} rules will be applied.
                    </div>
                  )}
                  {isExaminer && (
                    <select
                      className="rc-model-select"
                      value={rubricModelChoice}
                      disabled={regenBusy}
                      onChange={(e) => setRubricModelChoice(e.target.value)}
                      title="Model used to regenerate this rubric"
                      style={{ width: 220 }}
                    >
                      <optgroup label="Local">
                        {LOCAL_MODELS.map((m) => (
                          <option key={m.value} value={`local:${m.value}`}>{m.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="ChatGPT">
                        {OPENAI_MODELS.map((m) => (
                          <option key={m.value} value={`openai:${m.value}`}>{m.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini">
                        {GEMINI_MODELS.map((m) => (
                          <option key={m.value} value={`gemini:${m.value}`}>{m.label}</option>
                        ))}
                      </optgroup>
                    </select>
                  )}
                  {isExaminer && String(rubricModelChoice).startsWith("local:") && (
                    <select
                      className="rc-model-select"
                      value={rubricHost}
                      disabled={regenBusy}
                      onChange={(e) => setRubricHost(e.target.value)}
                      title="Which machine runs this local model"
                      style={{ width: 160 }}
                    >
                      <option value="gpu">Spc</option>
                      <option value="mac">Mac</option>
                    </select>
                  )}
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={regenerateAll}
                      disabled={regenLocked || !rulesReady}
                      title={editingBlockIndex != null
                        ? "Save or cancel the question edit first"
                        : !modalRuleSet
                          ? "Select a Subject Rules set first"
                          : !rulesReady
                            ? "This Subject Rules set has no rules yet"
                            : `Regenerate all ${modalParts.blocks.length} questions`}
                      style={{
                        padding: "6px 12px",
                        border: "none",
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #0f766e, #0d9488)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: regenLocked || !rulesReady ? "not-allowed" : "pointer",
                        opacity: regenLocked || !rulesReady ? 0.45 : 1,
                        whiteSpace: "nowrap"
                      }}
                    >
                      <i className={regenScope === "all" ? "fas fa-spinner fa-spin" : "fas fa-rotate"} style={{ marginRight: 6 }}></i>
                      {regenScope === "all" ? "Regenerating..." : `Regenerate all (${modalParts.blocks.length})`}
                    </button>
                    <button
                      type="button"
                      onClick={regenerateSelected}
                      disabled={regenLocked || !rulesReady || pickedCount === 0}
                      title={editingBlockIndex != null
                        ? "Save or cancel the question edit first"
                        : !modalRuleSet
                          ? "Select a Subject Rules set first"
                          : !rulesReady
                            ? "This Subject Rules set has no rules yet"
                            : pickedCount
                              ? `Regenerate ${pickedCount} selected question${pickedCount === 1 ? "" : "s"}`
                              : "Select questions to regenerate"}
                      style={{
                        padding: "6px 12px",
                        border: "none",
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: regenLocked || !rulesReady || pickedCount === 0 ? "not-allowed" : "pointer",
                        opacity: regenLocked || !rulesReady || pickedCount === 0 ? 0.45 : 1,
                        whiteSpace: "nowrap"
                      }}
                    >
                      <i className={regenScope === "selected" ? "fas fa-spinner fa-spin" : "fas fa-rotate"} style={{ marginRight: 6 }}></i>
                      {regenScope === "selected" ? "Regenerating..." : `Regenerate selected${pickedCount ? ` (${pickedCount})` : ""}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                padding: 24,
                background: "var(--panel-bg, #ffffff)",
              }}
            >
              {isEditing ? (
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  style={{
                    width: "100%",
                    height: "calc(100vh - 230px)",
                    minHeight: "320px",
                    padding: 16,
                    fontSize: 14,
                    lineHeight: 1.7,
                    border: "1px solid var(--border-color)",
                    borderRadius: 8,
                    background: "var(--surface, #f9fafb)",
                    color: "var(--text-primary)",
                    fontFamily: "monospace",
                    resize: "none",
                  }}
                />
              ) : severalRubrics ? (
                (() => {
                  const aligned = alignRubricRuns(rubricRuns);
                  const blockIn = (parts, id) => (parts.blocks || []).find((block) => block.id === id) || null;
                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${rubricRuns.length}, minmax(380px, 1fr))`,
                      columnGap: 16,
                      alignItems: "stretch"
                    }}>
                      {rubricRuns.map((run, index) => (
                        <div key={`stamp-${run.at || "rubric"}-${index}`} style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          alignSelf: "start",
                          background: "var(--panel-bg, #ffffff)",
                          padding: "2px 0 8px",
                          marginBottom: 8,
                          borderBottom: "1px solid var(--border-color, #e5e7eb)",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px 12px",
                          alignItems: "baseline"
                        }}>
                          <span style={{ fontWeight: 800 }}>
                            {run.latest ? `Rubric ${index + 1} · current` : `Rubric ${index + 1}`}
                          </span>
                          <span>{stageModelLabel(run.model, run.host) || "—"}</span>
                          <span style={{ color: "#64748b", fontWeight: 600 }}>
                            {run.at ? formatDate(run.at) : ""}
                          </span>
                        </div>
                      ))}
                      {aligned.parsed.some((parts) => parts.preamble.trim()) && aligned.parsed.map((parts, index) => (
                        <div key={`preamble-${index}`} style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-primary)" }}>
                          {parts.preamble.trim() ? <RubricMarkdown>{parts.preamble}</RubricMarkdown> : null}
                        </div>
                      ))}
                      {aligned.keys.map((id, rowIndex) => aligned.parsed.map((parts, col) => {
                        const block = blockIn(parts, id);
                        const run = rubricRuns[col];
                        let body = null;
                        if (run.latest && reviseOpen) {
                          const bi = modalParts.blocks.findIndex((item) => item.id === id);
                          if (bi >= 0) body = renderQuestionBlock(modalParts.blocks[bi], bi, false);
                        }
                        if (!body && block) {
                          body = (
                            <div className="rc-qblock">
                              <div className="rc-qblock__body">
                                <RubricMarkdown>{shownRubric(block.text)}</RubricMarkdown>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={`${id}-${col}`} className={`rc-qrow${rowIndex === 0 ? " is-first" : ""}${body ? "" : " is-empty"}`}>
                            {body}
                          </div>
                        );
                      }))}
                    </div>
                  );
                })()
              ) : renderCurrentRubric()}
            </div>

            {/* Modal Actions */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid var(--border-color)",
                flex: "0 0 auto",
                display: "flex",
                gap: 12,
                justifyContent: isEditing ? "space-between" : "flex-end",
              }}
            >
              {isEditing ? (
                <>
                  <button
                    onClick={cancelEditing}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "1px solid var(--border-color)",
                      background: "var(--bg-secondary)",
                      color: "var(--text-primary)",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "none",
                      background: "#4caf50",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  <button
                        onClick={() => copyForEduPage(stripQuestionPaperText(shownRubric(modalRubric.content)))}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "none",
                      background: "#FF9800",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 600,
                      marginRight: "auto",
                    }}
                  >
                    Copy for EduPage
                  </button>
                  {canEdit(modalRubric) && (
                    <>
                      <button
                        onClick={() => {
                          editInEditPage(shownRubric(modalRubric.content));
                          closeModal();
                        }}
                        style={{
                          padding: "10px 20px",
                          fontSize: 14,
                          borderRadius: 6,
                          border: "none",
                          background: "#9C27B0",
                          color: "white",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Edit in Edit Page
                      </button>
                      <button
                        onClick={startEditing}
                        style={{
                          padding: "10px 20px",
                          fontSize: 14,
                          borderRadius: 6,
                          border: "none",
                          background: "#2196F3",
                          color: "white",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Edit Here
                      </button>
                    </>
                  )}
                  {canManageAnyRubric(currentUser.role) && (
                    <button
                      onClick={() => {
                        loadRubricForUse(modalRubric.id);
                        closeModal();
                      }}
                      style={{
                        padding: "10px 20px",
                        fontSize: 14,
                        borderRadius: 6,
                        border: "none",
                        background: "#4caf50",
                        color: "white",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Use Rubric
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const source = isEditing ? restorePaperNotes(editedContent, modalRubric.content) : modalRubric.content;
                      setQuestionPaper(attachRubricCard(extractQuestionPaper(shownRubric(source)), modalRubric));
                    }}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "none",
                      background: "#0f766e",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    <i className="fa-solid fa-print" style={{ marginRight: 6 }}></i>
                    Question paper
                  </button>
                  <button
                    onClick={closeModal}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      borderRadius: 6,
                      border: "1px solid var(--border-color)",
                      background: "var(--bg-secondary)",
                      color: "var(--text-primary)",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <MessageBox 
        message={message} 
        onClose={() => setMessage({ show: false, text: '', type: 'info' })} 
      />
      
      <ConfirmDialog 
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
      <QuestionPaperPreview paper={questionPaper} onClose={() => setQuestionPaper(null)} />
      <SaveRubricModal
        isOpen={editInfoOpen}
        onClose={() => setEditInfoOpen(false)}
        onSave={saveRubricInfo}
        currentUser={currentUser}
        initialRubric={editInfoOpen ? modalRubric : null}
      />
    </div>
  );
}
